import { Button, Popover, Tooltip } from "@hellocarbo/lasagne";
import { ArrowRight, Ellipsis, PowerOffIcon, TrashIcon } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher, useParams } from "react-router";
import invariant from "tiny-invariant";

import { PortalContainerContext } from "@/contexts";
type EditActionsProps = {
  importDepositId: number;
  importDepositStatus: string;
  isScoreReadOnly: boolean;
  detailUrl: string;
};

const EditActions = ({
  importDepositId,
  importDepositStatus,
  isScoreReadOnly,
  detailUrl,
}: EditActionsProps) => {
  const { t } = useTranslation("importDeposit");
  const { scoreId } = useParams();
  const { container } = useContext(PortalContainerContext);
  const fetcher = useFetcher();

  invariant(scoreId, "No scoreId found!");

  const handleCancelClick = () => {
    const data = new FormData();
    data.set("scoreId", scoreId);
    data.set("importId", importDepositId.toString());
    data.set("actionType", "cancel");
    fetcher.submit(data, {
      method: "PUT",
    });
  };

  const handleDeleteClick = () => {
    const data = new FormData();
    data.set("scoreId", scoreId);
    data.set("importId", importDepositId.toString());
    data.set("actionType", "delete");
    fetcher.submit(data, {
      method: "PUT",
    });
  };

  const shouldDisplayImportActions =
    !isScoreReadOnly &&
    ["integrated", "accepted", "matching"].includes(importDepositStatus);

  const canBeDeleted = importDepositStatus === "integrated";
  const canBeCanceled = ["matching", "accepted"].includes(importDepositStatus);

  return (
    <td className={"py-sm grid grid-cols-2 items-center"}>
      <div className="w-full">
        {shouldDisplayImportActions && (
          <Popover
            align="center"
            side="left"
            sideOffset={4}
            portalContainer={container as Element}
            trigger={<Button Icon={Ellipsis} intent="primary" shape="link" />}
          >
            {canBeDeleted && (
              <Button
                shape="link"
                intent="negative"
                onClick={handleDeleteClick}
              >
                <TrashIcon />
                {t(`importDeposit:importAction.deleteCta`)}
              </Button>
            )}
            {canBeCanceled && (
              <Button
                shape="link"
                intent="negative"
                onClick={handleCancelClick}
              >
                <PowerOffIcon />
                {t(`importDeposit:importAction.cancelCta`)}
              </Button>
            )}
          </Popover>
        )}
      </div>

      <div className="w-full">
        <Tooltip
          content={t(`importDeposit:importAction.seeMoreCta`)}
          side="left"
          portalContainer={container as Element}
        >
          <Button
            as="a"
            href={detailUrl}
            Icon={ArrowRight}
            intent="primary"
            shape="soft"
          />
        </Tooltip>
      </div>
    </td>
  );
};

export default EditActions;
